import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';
import type { Select } from 'ember-power-select/components/power-select';
import type { PowerSelectGroupSignature } from 'ember-power-select/components/power-select/power-select-group';
import PowerSelectPowerSelectGroup from 'ember-power-select/components/power-select/power-select-group';

import type {
  Group,
  Option,
  SelectableCollapsedGroup,
} from '../../../../types/components/prism/combo-select-input.ts';
import PrismButton from '../../button.gts';
import PrismIcon from '../../icon.gts';

interface PrismComboSelectInputUnsafeCollapsibleGroupArgs {
  onGroupCollapseClick: () => void;
  // This component is highly dynamic and very hard to type, I haven't found a proper solution for it yet
  // Hence why it is just a generic component that accepts any argument
  /* eslint-disable @typescript-eslint/no-explicit-any */
  unsafe_afterBackOptionComponent?: ComponentLike<any>;
  unsafe_collapsibleGroupBackLabel?: string;
}

interface PrismComboSelectInputUnsafeCollapsibleGroupSignature extends PowerSelectGroupSignature<
  Option,
  Group<Option>
> {
  Args: PowerSelectGroupSignature<Option, Group<Option>>['Args'] &
    PrismComboSelectInputUnsafeCollapsibleGroupArgs;
}

export default class PrismComboSelectInputUnsafeCollapsibleGroup extends Component<PrismComboSelectInputUnsafeCollapsibleGroupSignature> {
  // If the property is set to false, the group is considered expanded, managed internally
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCollapsed() {
    return (
      'unsafe_isCollapsed' in this.args.group &&
      this.args.group.unsafe_isCollapsed
    );
  }

  // Once this property is present, it's considered a collapsed group
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCollapsible() {
    return 'unsafe_isCollapsed' in this.args.group;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSelectable() {
    return (
      'unsafe_isSelectable' in this.args.group &&
      this.args.group.unsafe_isSelectable
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get select() {
    return this.args.select as Select<Option, Group<Option>>;
  }

  @action
  handleGroupExpandClick(event: MouseEvent): void {
    // The expand button is inside of the clickable <li> element,
    // so we need to stop the event from propagating to prevent the group from being selected.
    event.stopPropagation();

    this.select.actions.choose(this.args.group as unknown as Option);
  }

  @action
  handleGroupLabelClick(): void {
    // Remove the `unsafe_isCollapsed` property  completely and pick the groupName type
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { unsafe_isCollapsed, groupName, ...option } = this.args
      .group as SelectableCollapsedGroup<Option>;

    this.select.actions.choose({
      ...option,
      label: groupName,
    });
  }

  @action
  handleMouseEnter(): void {
    // Makes sure no other options are highlighted by power-select's internal API
    this.args.select.actions.highlight(null);
  }

  <template>
    {{! template-lint-disable require-mandatory-role-attributes require-presentational-children }}
    {{#if this.isCollapsible}}
      {{#if this.isCollapsed}}
        {{#if this.isSelectable}}
          <li
            class="unsafe-collapsible-selectable-group truncate
              {{@group.unsafe_className}}"
            role="option"
            {{on "click" this.handleGroupLabelClick}}
            {{on "mouseenter" this.handleMouseEnter}}
          >
            <div class="unsafe-collapsible-group__content">
              {{#if @group.icon}}
                <PrismIcon @name={{@group.icon}} @size="small" />
              {{/if}}

              <p class="text-300--regular">{{@group.groupName}}</p>
            </div>
            <PrismButton
              @buttonType="secondary"
              @icon="chevron-right"
              @onClick={{this.handleGroupExpandClick}}
              @size="small"
              data-test-id="collapsible-group-expand-button"
            />
          </li>
        {{else}}
          <li
            class="unsafe-collapsible-group"
            role="option"
            {{on "click" this.handleGroupExpandClick}}
            {{on "mouseenter" this.handleMouseEnter}}
          >
            <p
              class="text-300--regular unsafe-collapsible-group__label truncate"
            >
              {{@group.groupName}}
            </p>
            <PrismIcon @name="chevron-right" @size="small" />
          </li>
        {{/if}}
      {{else}}
        <li
          class="unsafe-collapsible-group-back-option"
          role="option"
          {{on "click" @onGroupCollapseClick}}
          {{on "mouseenter" this.handleMouseEnter}}
        >
          <div class="unsafe-collapsible-group-back-option__content">
            <PrismIcon @name="chevron-left" @size="small" />
            <p class="text-300--regular truncate">
              {{@unsafe_collapsibleGroupBackLabel}}
            </p>
          </div>
          {{#if @unsafe_afterBackOptionComponent}}
            {{@unsafe_afterBackOptionComponent}}
          {{/if}}
        </li>
        <li
          class="unsafe-collapsible-group unsafe-collapsible-group--expanded"
          role="option"
        >
          <p class="text-300--regular truncate">
            {{@group.groupName}}
          </p>
        </li>
        {{yield}}

      {{/if}}
    {{else}}
      <PowerSelectPowerSelectGroup @group={{@group}} @select={{@select}}>
        {{yield}}
      </PowerSelectPowerSelectGroup>
    {{/if}}
  </template>
}
